/*
https://git.acronis.com/projects/STRG/repos/vstorage-ui-specs/pull-requests/20/diff
Reconfiguration wizard have to be shown only after state transits to 'testing'
*/

import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import ROLES from '@/common/constants/roles.constants';
import {
  STATUS_ICON_CHECK_O,
  STATUS_ICON_CLOCK,
  STATUS_ICON_INACTIVE, STATUS_ICON_LOADING,
  STATUS_ICON_TIMES_O, STATUS_ICON_WARNING
} from '@/common/utils/statuses';

export const OPERATIONS = {
  TRAFFIC_TYPE_ASSIGNMENT: 'traffic-type-assignment',
  NETWORK_MIGRATION: 'migration',
  NETWORK_RELOCATION: 'network-relocation'
};

export const OPERATIONS_MAP = {
  'traffic-type-assignment-details': OPERATIONS.TRAFFIC_TYPE_ASSIGNMENT,
  'network-migration-details': OPERATIONS.NETWORK_MIGRATION,
  'network-relocation-details': OPERATIONS.NETWORK_RELOCATION
};

export const STATUSES = {
  // silent processes statuses
  silent: ['new', 'preparing', 'assigning'],
  success: ['done', 'interrupted'],
  error: ['halted', 'failed-to-revert', 'failed-to-apply', 'test-failed', 'failed-to-store', 'failed-to-restore'],
  inProgress: ['testing', 'applying', 'reverting', 'waiting-for-shutdown', 'storing']
};

export const MSG_ID_MAP = computedLocale(() => ({
  'interface-is-down': i18n.t('Interface is down.'),
  'no-connectivity-with': i18n.t('No connectivity with target network.'),
  'no-reconfiguration-required': i18n.t('Network reconfiguration not required.'),
  'source-network-not-found': i18n.t('Source network not found. It was deleted from database.'),
  'target-network-not-found': i18n.t('Target network not found. It was deleted from database.'),
  'network-not-found': i18n.t('Network not found. It was deleted from database.'),
  'multiple-source-networks-found': i18n.t('Traffic type is assigned to multiple source networks.'),
  'irreversible-state': i18n.t('Cannot revert network migration. Either continue reconfiguration or call to support center.'),
  'no-compute-deployed': i18n.t('Compute cluster was not deployed.'),
  'compute-was-destroyed': i18n.t('Compute cluster was destroyed.'),
  'included-in-ha': i18n.t('Node is included in management HA.'),
  'no-target-interface': i18n.t('Target network interface not found on the node.'),
  'cannot-assign-traffic-type': i18n.t('Traffic type cannot be assigned to the target network.'),
  'cannot-unassign-traffic-type': i18n.t('Traffic type cannot be unassigned from the source network.'),
  'cannot-reassign-traffic-type': i18n.t('Traffic type cannot be reassigned to the other network.'),
  'cannot-initialize-connectivity-check': i18n.t('Cannot initialize connectivity check.'),
  'cannot-finalize-connectivity-check': i18n.t('Cannot finalize connectivity check.'),
  'improperly-resolved-hostname': i18n.t('Hostname resolved improperly.'),
  'cannot-configure-abgw': i18n.t('Cannot configure Acronis Backup Gateway services.'),
  'cannot-configure-ostor-agentd': i18n.t('Cannot configure Object storage agent.'),
  'cannot-configure-ostor-cfgd': i18n.t('Cannot configure Object storage configuration service.'),
  'source-addresses-still-resolved': i18n.t('Source addresses are still resolvable.'),
  'target-addresses-not-resolved': i18n.t('Target addresses are not resolved.'),
  'cannot-store-shaman-roles': i18n.t('Cannot store Single high availability manager\'s roles.'),
  'cannot-leave-shaman': i18n.t('Cannot disable high availability support for the node.'),
  'cannot-configure-mds': i18n.t('Cannot configure metadata server.'),
  'cannot-join-in-shaman': i18n.t('Cannot join the storage node to the Single high availability manager (Shaman) cluster.'),
  'cannot-restore-shaman-roles': i18n.t('Cannot restore Single high availability manager\'s roles.'),
  'cannot-start-shaman-service': i18n.t('Cannot start Single high availability manager.'),
  'compute-is-being-reconfigured': i18n.t('Compute cluster is being reconfigured.'),
  'cannot-configure-compute-endpoints': i18n.t('Cannot configure compute cluster endpoints.'),
  'cannot-reconfigure-compute': i18n.t('Cannot reconfigure Compute cluster.'),
  'cannot-reconfigure-neutron': i18n.t('Cannot reconfigure OpenStack Neutron.'),
  'cannot-reconfigure-nginx-compute': i18n.t('Cannot reconfigure NGINX.'),
  'cannot-restore-source-interface': i18n.t('Cannot restore network interface\'s old settings.'),
  'cannot-place-interface-in-mixed-mode': i18n.t('Cannot configure network interface for migration.'),
  'cannot-apply-new-address': i18n.t('Cannot migrate network interface to the new addresses.'),
  'no-connectivity': i18n.t('No connectivity.'),
  'multiple-ip-addresses': i18n.t('Multiple IP addresses found on the network interface.'),
  'network-differs': i18n.t('Interface\'s subnet does not match with the network.'),
  'no-ip-address': i18n.t('No IP addresses are set to network interface.'),
  'address-collision-detected': i18n.t('Address collision detected.'),
  'gateway-differs': i18n.t('None or single gateway can be set for the whole network.'),
  'gateway-network-differs': i18n.t('Gateway does not belong to the network.'),
  'non-empty-gateway': i18n.t('Only no gateway permitted for the network.'),
  'might-be-out-of-sync': i18n.t('Addresses might be out of sync. Change interface setting manually.'),
  'slow-interface': i18n.t('The interface is the slower one in the cluster.'),
  'target-interface-slower': i18n.t('Target interface is slower than source one.'),
  'mtu-differs': i18n.t('Interface\'s MTU differs from MTUs of other members of the network.'),
  'speed-undefined': i18n.t('Interface\'s speed is undefined.'),
  'mtu-undefined': i18n.t('Interface\'s MTU is undefined.'),
  'new-cfgd-addresses-not-resolved': i18n.t('New addresses of Object storage configuration service are not resolved.'),
  'node-is-installing': i18n.t('The node is in installing state.'),
  'old-cfgd-addresses-still-resolved': i18n.t('Old addresses of Object storage configuration service are still resolvable.'),
  'improperly-resolved-traffic-type-based-hostname': i18n.t('Traffic type-based hostname resolved improperly (refer to the log files for more information).'),
  'cannot-update-resolv-conf': i18n.t('Cannot update resolv.conf on the node.'),
  'test-expired': i18n.t('Connectivity check results have expired. Please run the check again.'),
  'no-assignment-required': i18n.t('Traffic type assignment is not required.'),
  'cannot-resolve-cfgd-hostname': i18n.t('Cannot resolve hostname of Object storage configuration service (refer to the log files for more information).'),
  'improperly-resolved-rabbitmq-hostname': i18n.t('RabbitMQ hostname resolved improperly (refer to the log files for more information).'),
  'cannot-restart-octavia': i18n.t('Cannot restart OpenStack Octavia')
}));

export const REASSIGNMENT_WARNING = computedLocale(() => ({
  [ROLES.STORAGE]: i18n.t('Reassignment of the {name} traffic type will result in downtime of the storage services and must be performed with a fully validated new configuration.', { name: ROLES.STORAGE }),
  [ROLES.MANAGEMENT]: i18n.t('Reassignment of the {name} traffic type will result in downtime of the storage services and must be performed with a fully validated new configuration.', { name: ROLES.MANAGEMENT }),
  [ROLES.OSTOR_PRIVATE]: i18n.t('Reassignment of the {name} traffic type may result in downtime of the storage services and must be performed with a fully validated new configuration.', { name: ROLES.OSTOR_PRIVATE }),
  [ROLES.COMPUTE_PRIVATE]: i18n.t('Reassignment of the {name} traffic type may result in downtime of the compute service and must be performed with a fully validated new configuration.', { name: ROLES.COMPUTE_PRIVATE }),
  [ROLES.COMPUTE_API]: i18n.t('Reassignment of the {name} traffic type may result in downtime of the compute service and must be performed with a fully validated new configuration.', { name: ROLES.COMPUTE_API }),
  [ROLES.NBD]: i18n.t('Reassignment of the {name} traffic type may result in downtime of the compute service and must be performed with a fully validated new configuration.', { name: ROLES.NBD }),
  [ROLES.ABGW_PRIVATE]: i18n.t('Reassignment of the {name} traffic type may result in downtime of the storage services and must be performed with a fully validated new configuration.', { name: ROLES.ABGW_PRIVATE })
}));

export const STATUSES_MAP = computedLocale(() => ({
  offline: {
    icon: STATUS_ICON_INACTIVE,
    text: i18n.t('Not connected')
  },
  completed: {
    icon: STATUS_ICON_CHECK_O,
    text: i18n.t('Completed')
  },
  pending: {
    icon: STATUS_ICON_CLOCK,
    text: i18n.t('Pending')
  },
  error: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Error')
  },
  reassigning: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Reassigning...')
  },
  reverting: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Reverting...')
  },
  'node-offline': {
    icon: STATUS_ICON_INACTIVE
  },
  success: {
    icon: STATUS_ICON_CHECK_O,
  },
  loading: {
    icon: STATUS_ICON_LOADING
  },
  warn: {
    icon: STATUS_ICON_WARNING,
  },
  failed: {
    icon: STATUS_ICON_TIMES_O,
  }
}));
